import type { PlanInfo } from '../hooks/useSubscription';

export type PaidPlanType = 'pro' | 'business' | 'agency';

export interface PaidPlanMarketing {
  designsPerMonth: string;
  intro: string;
  bullets: string[];
  cta: string;
  featured?: boolean;
}

export interface CreditUsageItem {
  credits: string;
  title: string;
  description: string;
}

export type DisplayPaidPlan = PlanInfo & {
  type: PaidPlanType;
  marketing: PaidPlanMarketing;
};

export const PLAN_CAPABILITY_COPY =
  'Every plan includes full access to Miles. No coding agent subscription required. Pick the usage you need and use your credits across one site or many.';

export const CREDIT_USAGE_ITEMS: CreditUsageItem[] = [
  {
    credits: '~3',
    title: 'Ask a question',
    description: 'A quick back-and-forth with Miles about your site.',
  },
  {
    credits: '~90',
    title: 'Make an edit',
    description: 'Restyle or adjust a section on a site, or lay out a new one.',
  },
  {
    credits: '~250',
    title: 'Build a page',
    description: 'A finished, polished page built from your design direction.',
  },
  {
    credits: '~2,000',
    title: 'A complete site',
    description: 'Typical total for a full multi-page build, revisions included.',
  },
];

export const PAID_PLAN_ORDER: PaidPlanType[] = ['pro', 'business', 'agency'];

export const PAID_PLAN_MARKETING: Record<PaidPlanType, PaidPlanMarketing> = {
  pro: {
    designsPerMonth: '2+ designs a month',
    intro:
      'Miles can help you design and redesign entire sites, from initial concept to a finished site. Plenty of room for revisions and ongoing edits.',
    bullets: [
      'Best for designing and redesigning entire sites',
      'Full workflow: design directions through finished site',
      'Room for editing and design revisions across your sites',
      'Email support',
    ],
    cta: 'Upgrade to Pro',
    featured: true,
  },
  business: {
    designsPerMonth: '5+ designs a month',
    intro:
      'Lean on Miles for significant client work across multiple sites. Ship more every week, with plenty of headroom for heavy iteration.',
    bullets: [
      'Best for high-volume site creation and editing',
      'Build across multiple client sites at once',
      'Room for heavy editing and design revisions across many sites',
      'Priority email support',
    ],
    cta: 'Upgrade to Business',
  },
  agency: {
    designsPerMonth: '8+ designs a month',
    intro:
      'Invite unlimited teammates to work across your agency sites. Everyone uses the same shared credit pool and can work together with Miles.',
    bullets: [
      'Best for agency-wide access to Miles',
      'Shared credit pool with unlimited seats',
      'Multiple users of Miles on a single site',
      'Priority support with a direct channel',
    ],
    cta: 'Upgrade to Agency',
  },
};

export function getDisplayPaidPlans(plans: PlanInfo[]): DisplayPaidPlan[] {
  return PAID_PLAN_ORDER.flatMap((type) => {
    const plan = plans.find((candidate) => candidate.type === type);
    if (!plan) return [];

    return [
      {
        ...plan,
        type,
        marketing: PAID_PLAN_MARKETING[type],
      },
    ];
  });
}
